import {
  Component,
  ElementRef,
  ViewChild,
  NgZone,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import gsap from 'gsap';
import { DialogFlowResult, ListeningStatus, ListeningStatusKey } from 'src/app/data/type/audio';
import { Parameter } from 'src/app/data/type/parameter';
import { VoiceSearchService } from 'src/app/service/voice-search.service';
import { LocalizationService } from 'src/app/service/localization.service';
import { RemoteControlService } from 'src/app/service/remote-control.service';
import { TvFeature } from 'src/app/data/enum/tv-feature';
import { Subscription } from 'rxjs';
import FeaturePage from 'src/app/core/feature-page';
import createInitialPageTimeline from '../../utils/animation/initial-page-timeline';
import { ActivatedRoute } from '@angular/router';
import { JsonKey } from 'src/app/data/type/locale-copy-key';
import endPageTimeline from '../../utils/animation/end-page-timeline';
import { voiceSearchPageAnimations } from './animation';
import { EndScreenButton } from 'src/app/service/progress.service';
import { AnalyticsEvent, triggerEvent } from 'src/app/utils/analytics';
import { VoiceSearchFeature, VoiceSearchFeatureKey } from './voice-search-page.types';
import { EndScreenCopy } from 'src/app/data/enum/end-screen';
import { RotationService } from 'src/app/service/rotation.service';
import { Selectors } from 'src/app/data/enum/selectors';
import { ParameterService } from 'src/app/service/parameter.service';

const localEvents = {
  [VoiceSearchFeature['lights']]: AnalyticsEvent.voiceDimLights,
  [VoiceSearchFeature['action-movies']]: AnalyticsEvent.voiceActionMovies,
  [VoiceSearchFeature['front-door']]: AnalyticsEvent.voiceFrontDoor,
  [VoiceSearchFeature['front-door']]: AnalyticsEvent.voiceChildrens,
};

const actionsData = {
  [VoiceSearchFeature['lights']]: {
    title: JsonKey.END_SCREEN_VOICE_LIGHTS_TITLE,
    copy: JsonKey.END_SCREEN_VOICE_LIGHTS_COPY,
  },
  [VoiceSearchFeature['action-movies']]: {
    title: JsonKey.END_SCREEN_VOICE_ACTION_TITLE,
    copy: JsonKey.END_SCREEN_VOICE_ACTION_COPY,
  },
  [VoiceSearchFeature['front-door']]: {
    title: JsonKey.END_SCREEN_VOICE_DOOR_TITLE,
    copy: JsonKey.END_SCREEN_VOICE_DOOR_COPY,
  },
  [VoiceSearchFeature['children-shows']]: {
    title: JsonKey.END_SCREEN_VOICE_CHILDREN_TITLE,
    copy: JsonKey.END_SCREEN_VOICE_CHILDREN_COPY,
  },
};

@Component({
  selector: 'app-voice-search-page',
  templateUrl: './voice-search-page.component.html',
  styleUrls: ['./voice-search-page.component.scss'],
})
export class VoiceSearchPageComponent
  extends FeaturePage
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('frontDoorVideo') frontDoorVideo: ElementRef;
  @ViewChild('voiceRef') containerElementRef: ElementRef;
  public isSttEnabled = this.route.snapshot.queryParams[Parameter['disable-stt']] !== 'true';
  public speechResult: string = '';
  public isListening: boolean = false;
  public isLoading: boolean = false;
  public disableIntroButtons: boolean = true;
  public endScreenTranslations: EndScreenCopy;
  private speechResultsEventSubscription: Subscription;
  private statusEventSubscription: Subscription;
  private dialogFlowEventSubscription: Subscription;
  private isPortraitModalOpen: boolean = true;
  private introPageTimeline: GSAPTimeline = gsap.timeline({
    paused: false,
  });
  public tooltipCopy: string;

  private activeFeature: VoiceSearchFeatureKey | null = null;
  public endScreenButtons: Array<EndScreenButton> = [
    {
      restart: true,
      callback: () => {
        voiceSearchPageAnimations[this.activeFeature]
          .outroAnimation(this.rotationService.isLandscape)
          .then(() => {
            this.toggleIntroButtons(false);
            this.handleAriaHidden(this.activeFeature, true);
          });
        this.handlePortraitModal();
        triggerEvent(AnalyticsEvent.voiceSearchEndScreenSaySomethingElse);
      },
      copy: this.localizationService.translate(JsonKey.END_SCREEN_VOICE_TRY_AGAIN),
      feature: TvFeature.VOICE,
    },
  ];
  public videoTitle: string = this.localizationService.translate(JsonKey.VOICE_VIDEO_TITLE);

  constructor(
    private route: ActivatedRoute,
    private voiceSearchService: VoiceSearchService,
    protected localizationService: LocalizationService,
    protected remoteControlService: RemoteControlService,
    protected parameterSercice: ParameterService,
    protected zone: NgZone,
    public rotationService: RotationService,
  ) {
    super(localizationService, remoteControlService, parameterSercice, zone, TvFeature.VOICE);
  }

  handleLastStep() {}
  handleRemoteSignal() {}

  handleSpeechResults(result: string) {
    this.speechResult = result;
  }

  handleSpeechStatusChange(status: ListeningStatusKey) {
    // The user hasn't given acces or there is no microphone.
    if (status === ListeningStatus.ERROR) {
      this.isSttEnabled = false;
      return;
    }

    this.isLoading = status === ListeningStatus.LOADING;
    this.isListening = status === ListeningStatus.LISTENING;

    if (this.isListening) {
      this.speechResult = '';
      triggerEvent(AnalyticsEvent.voiceFirstInteraction);
    }
  }

  updateEndScreenCopy(copy?: EndScreenCopy) {
    this.endScreenTranslations = copy || actionsData[this.activeFeature];
  }

  handleVoiceSearchFeatures(result: DialogFlowResult) {
    if (result.text in VoiceSearchFeature) {
      this.activeFeature = result.text as VoiceSearchFeatureKey;
      this.updateEndScreenCopy();
      if (
        result.text !== VoiceSearchFeature.lights &&
        result.text !== VoiceSearchFeature['front-door']
      )
        this.playIntroAnimation(this.activeFeature);
    } else {
      this.activeFeature = null;
    }
  }

  handleAudioEnded() {
    if (this.activeFeature) this.playIntroAnimation(this.activeFeature);
  }

  ngOnInit(): void {
    this.tooltipCopy = this.localizationService.translate(JsonKey.VOICE_COACHMARK);
    this.speechResultsEventSubscription = this.voiceSearchService.speechResultsEvent.subscribe(
      this.handleSpeechResults.bind(this),
    );
    this.statusEventSubscription = this.voiceSearchService.statusEvent.subscribe(
      this.handleSpeechStatusChange.bind(this),
    );
    this.dialogFlowEventSubscription = this.voiceSearchService.dialogFlowEvent.subscribe(
      this.handleVoiceSearchFeatures.bind(this),
    );
    this.dialogFlowEventSubscription = this.voiceSearchService.audioEndedEvent.subscribe(
      this.handleAudioEnded.bind(this),
    );
  }

  toggleIntroButtons(state: boolean): void {
    this.zone.run(() => {
      this.disableIntroButtons = state;
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.introPageTimeline
      .add(createInitialPageTimeline(this.containerElementRef, this.isAbstract).play())
      .then(() => {
        this.toggleIntroButtons(false);
      });
  }

  handlePortraitModal() {
    if (!this.rotationService.isLandscape) {
      gsap.to(Selectors.VOICE_SEARCH_MODAL, {
        visibility: this.isPortraitModalOpen ? 'hidden' : 'visible',
      });
      this.isPortraitModalOpen = !this.isPortraitModalOpen;
    }
  }
  playIntroAnimation(feature: VoiceSearchFeatureKey) {
    this.activeFeature = feature;
    this.updateEndScreenCopy(actionsData[feature]);
    voiceSearchPageAnimations[feature]
      .introAnimation(this.frontDoorVideo.nativeElement, this.rotationService.isLandscape, () => {
        this.handleSpeechResults('');
        this.toggleIntroButtons(true);
        this.handlePortraitModal();
        this.handleAriaHidden(feature, false);
      })
      .add(
        endPageTimeline(this.containerElementRef, true).play(),
        voiceSearchPageAnimations[feature]
          .introAnimation(this.frontDoorVideo.nativeElement, this.rotationService.isLandscape)
          .duration() + 2,
      )
      .then(() => {
        triggerEvent(AnalyticsEvent.voiceSearchEndScreenOpen);
      });
    triggerEvent(localEvents[feature]);
  }

  handleAriaHidden(feature: string, state: boolean) {
    this.containerElementRef.nativeElement
      .querySelector(`[data-animation="${feature}"]`)
      .setAttribute('aria-hidden', `${state}`);
  }

  handleIntroButtonClick(feature: VoiceSearchFeatureKey) {
    this.playIntroAnimation(feature);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.speechResultsEventSubscription.unsubscribe();
    this.statusEventSubscription.unsubscribe();
    this.dialogFlowEventSubscription.unsubscribe();
  }
}
